import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDataMode } from '../../contexts/DataModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  FolderKanban, FileCheck, DollarSign, TrendingUp,
  AlertCircle, ChevronRight, Rss, BarChart3, Inbox,
  Calendar, Building2, MapPin, Mail, X, CheckCircle2,
  Trophy, Zap, Star, Gem, Download,
  AlertTriangle, Activity, Sparkles, User2, ArrowRight, Send, Flame, Globe, Wrench, Target,
  Loader2, MessageSquare,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import TestimonialForm from '../../components/TestimonialForm';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── VERIFIED CERTIFICATE PDF ─────────────────────────────────── */
const downloadVerifiedCertificate = (firmName, verifiedUntil) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = 297, H = 210;

  // Deep navy background
  doc.setFillColor(10, 22, 54);
  doc.rect(0, 0, W, H, 'F');

  // Gold border frame (outer)
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(2);
  doc.rect(8, 8, W - 16, H - 16, 'S');
  // Gold border frame (inner)
  doc.setLineWidth(0.5);
  doc.rect(12, 12, W - 24, H - 24, 'S');

  // Corner ornaments (gold dots)
  [[16,16],[W-16,16],[16,H-16],[W-16,H-16]].forEach(([x,y]) => {
    doc.setFillColor(212, 175, 55);
    doc.circle(x, y, 2, 'F');
  });

  // EXPO STAND header
  doc.setFontSize(10);
  doc.setTextColor(212, 175, 55);
  doc.setFont('helvetica', 'bold');
  doc.text('EXPO STAND', W / 2, 30, { align: 'center' });

  // Thin gold divider
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.3);
  doc.line(W/2 - 40, 34, W/2 + 40, 34);

  // Certificate title
  doc.setFontSize(28);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('CERTIFICATE OF VERIFICATION', W / 2, 60, { align: 'center' });

  // Subtitle
  doc.setFontSize(11);
  doc.setTextColor(180, 190, 210);
  doc.setFont('helvetica', 'normal');
  doc.text('This is to certify that the following organization has been', W / 2, 74, { align: 'center' });
  doc.text('officially verified as a trusted partner on the EXPO STAND platform.', W / 2, 81, { align: 'center' });

  // Gold seal circle
  doc.setFillColor(212, 175, 55);
  doc.circle(W / 2, 120, 24, 'F');
  doc.setFillColor(10, 22, 54);
  doc.circle(W / 2, 120, 20, 'F');
  doc.setFillColor(212, 175, 55);
  doc.circle(W / 2, 120, 16, 'F');
  // Checkmark text inside seal
  doc.setFontSize(18);
  doc.setTextColor(10, 22, 54);
  doc.setFont('helvetica', 'bold');
  doc.text('\u2713', W / 2, 126, { align: 'center' });

  // Firm name
  doc.setFontSize(24);
  doc.setTextColor(212, 175, 55);
  doc.setFont('helvetica', 'bold');
  const nameY = 105;
  doc.text(firmName, W / 2, nameY, { align: 'center' });

  // Gold underline below firm name
  const nameWidth = doc.getTextWidth(firmName);
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.8);
  doc.line(W/2 - nameWidth/2, nameY + 2, W/2 + nameWidth/2, nameY + 2);

  // Dates
  const issuedDate = new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  const expiryDate = verifiedUntil ? new Date(verifiedUntil).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

  doc.setFontSize(9);
  doc.setTextColor(180, 190, 210);
  doc.setFont('helvetica', 'normal');
  // Left date
  doc.text('Issue Date', 80, 152, { align: 'center' });
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(issuedDate, 80, 160, { align: 'center' });

  // Right date
  doc.setFontSize(9);
  doc.setTextColor(180, 190, 210);
  doc.setFont('helvetica', 'normal');
  doc.text('Valid Until', W - 80, 152, { align: 'center' });
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(expiryDate, W - 80, 160, { align: 'center' });

  // Vertical gold dividers around dates
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.3);
  doc.line(W/2 - 60, 147, W/2 - 60, 165);
  doc.line(W/2 + 60, 147, W/2 + 60, 165);

  // Center badge label
  doc.setFontSize(8);
  doc.setTextColor(212, 175, 55);
  doc.setFont('helvetica', 'bold');
  doc.text('VERIFIED PARTNER', W / 2, 155, { align: 'center' });
  doc.setFontSize(7);
  doc.setTextColor(140, 155, 180);
  doc.setFont('helvetica', 'normal');
  doc.text('expostand.com', W / 2, 162, { align: 'center' });

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(100, 115, 140);
  doc.text('This certificate is issued by EXPO STAND and confirms official verification status.', W / 2, 185, { align: 'center' });
  doc.text('Verify online at expostand.com', W / 2, 191, { align: 'center' });

  doc.save(`${firmName.replace(/\s+/g,'-')}-verified-certificate.pdf`);
  toast.success('Sertifika indirildi!');
};


const MOCK_DATA = {
  stats: { activeProjects: 4, pendingProposals: 7, monthlyRevenue: 284000, winRate: 62 },
  revenueChart: [
    { month: 'Sep', revenue: 180000 }, { month: 'Oct', revenue: 220000 },
    { month: 'Nov', revenue: 195000 }, { month: 'Dec', revenue: 260000 },
    { month: 'Jan', revenue: 284000 }, { month: 'Feb', revenue: 310000 },
  ],
  activeProjects: [
    { id: 'prj_001', client: 'Global Exhibitions Inc.', brief: 'Hannover Messe 2025', value: 79000, currency: 'EUR', deadline: '2025-03-01', progress: 65, status: 'in_progress' },
    { id: 'prj_002', client: 'Arab Health Org.', brief: 'Arab Health Dubai 2025', value: 88000, currency: 'USD', deadline: '2025-01-28', progress: 90, status: 'final_stage' },
    { id: 'prj_003', client: 'Barcelona Tech', brief: 'MWC 2025 Stand', value: 138000, currency: 'EUR', deadline: '2025-02-10', progress: 30, status: 'in_progress' },
  ],
  recentBriefs: [
    { id: 'br_new_1', title: 'Cityscape Dubai 2025', budget_max: 95000, currency: 'USD', city: 'Dubai', deadline: '2025-02-20', match_score: 92 },
    { id: 'br_new_2', title: 'Automechanika Frankfurt', budget_max: 120000, currency: 'EUR', city: 'Frankfurt', deadline: '2025-03-05', match_score: 78 },
    { id: 'br_new_3', title: 'Arab Health Dubai', budget_max: 85000, currency: 'USD', city: 'Dubai', deadline: '2025-02-28', match_score: 85 },
    { id: 'br_new_4', title: 'Paris Food Exhibition', budget_max: 60000, currency: 'EUR', city: 'Paris', deadline: '2025-03-15', match_score: 64 },
  ],
};

const STATUS_CONFIG = {
  in_progress: { color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
  final_stage: { color: 'bg-orange-100 text-orange-700', label: 'Final Stage' },
  completed:   { color: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
};

const ContractorDashboard = () => {
  const { isMockMode } = useDataMode();
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receivedBriefs, setReceivedBriefs] = useState([]);
  const [receivedLoading, setReceivedLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [respondingInv, setRespondingInv] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [limitBannerDismissed, setLimitBannerDismissed] = useState(false);
  const [activityStreak, setActivityStreak] = useState(null);
  const [coverageNudgeDismissed, setCoverageNudgeDismissed] = useState(
    () => localStorage.getItem('coverage_nudge_dismissed') === '1'
  );
  const [featuredStatus, setFeaturedStatus] = useState(null);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [verifiedStatus, setVerifiedStatus] = useState(null);
  const [verifiedLoading, setVerifiedLoading] = useState(false);
  const [pendingCommissions, setPendingCommissions] = useState([]);
  const [commissionLoading, setCommissionLoading] = useState({});
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const [aiRecs, setAiRecs] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 500));
        setData(MOCK_DATA);
      } else {
        try {
          const [projectsRes, proposalsRes, openBriefsRes] = await Promise.allSettled([
            axios.get(`${API}/contractor/projects`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${API}/contractor/proposals`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${API}/contractor/briefs/open`, { headers: { Authorization: `Bearer ${token}` } }),
          ]);
          const projects  = projectsRes.status  === 'fulfilled' ? (projectsRes.value.data?.items  || []) : [];
          const proposals = proposalsRes.status === 'fulfilled' ? (proposalsRes.value.data?.items || []) : [];
          const openBriefs = openBriefsRes.status === 'fulfilled' ? (openBriefsRes.value.data?.items || []) : [];
          const totalBriefs = openBriefsRes.status === 'fulfilled' ? (openBriefsRes.value.data?.total || openBriefs.length) : 0;
          const accepted  = proposals.filter(p => p.status === 'accepted').length;
          const winRate   = proposals.length > 0 ? Math.round((accepted / proposals.length) * 100) : 0;

          // Compute real monthly revenue trend from accepted proposals (last 6 months)
          const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const now = new Date();
          const revenueChart = [];
          for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthProps = proposals.filter(p => {
              if (!p.created_at) return false;
              const pd = new Date(p.created_at);
              return pd.getFullYear() === d.getFullYear() && pd.getMonth() === d.getMonth();
            });
            const revenue = monthProps
              .filter(p => p.status === 'accepted')
              .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
            revenueChart.push({ month: MONTH_NAMES[d.getMonth()], revenue });
          }

          setData({
            stats: {
              activeProjects:    projects.filter(p => p.status !== 'completed').length,
              pendingProposals:  proposals.filter(p => p.status === 'pending').length,
              monthlyRevenue:    projects.reduce((a, p) => a + (p.value || 0), 0),
              winRate,
              totalOpenBriefs:   totalBriefs,
            },
            revenueChart,
            activeProjects: projects.filter(p => p.status !== 'completed').slice(0, 3),
            recentBriefs: openBriefs.slice(0, 4).map(b => ({
              id: b.id, title: b.event?.show || b.id, budget_max: b.budget?.max,
              currency: b.budget?.currency || 'EUR', city: b.event?.city, deadline: b.deadline_at,
              match_score: b.match_score || null,
            })),
          });

          // Non-blocking AI brief recommendations (top 5 briefs → AI scored, sessionStorage cached 1h)
          if (openBriefs.length > 0) {
            const CACHE_KEY = `ai_recs_${token?.slice(-8)}`;
            const CACHE_TTL = 60 * 60 * 1000; // 1 hour
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (cached) {
              try {
                const { data, ts } = JSON.parse(cached);
                if (Date.now() - ts < CACHE_TTL) { setAiRecs(data); setAiLoading(false); return; }
              } catch { /* ignore corrupt cache */ }
            }
            setAiLoading(true);
            const briefIds = openBriefs.slice(0, 5).map(b => b.id);
            axios.post(`${API}/contractor/briefs/ai-match`, { brief_ids: briefIds }, { headers: { Authorization: `Bearer ${token}` } })
              .then(res => {
                const matches = res.data?.matches || {};
                const recs = openBriefs.slice(0, 5)
                  .map(b => ({
                    id: b.id, title: b.event?.show || b.id,
                    city: b.event?.city, country: b.event?.country,
                    budget_max: b.budget?.max, currency: b.budget?.currency || 'EUR',
                    ai_score: matches[b.id]?.score || 0,
                    ai_reason: matches[b.id]?.reason || '',
                  }))
                  .filter(b => b.ai_score >= 3)
                  .sort((a, b) => b.ai_score - a.ai_score)
                  .slice(0, 4);
                setAiRecs(recs);
                sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: recs, ts: Date.now() }));
              })
              .catch(() => {})
              .finally(() => setAiLoading(false));
          }
        } catch {
          setError('Could not connect to API. Showing mock data.');
          setData(MOCK_DATA);
        }
      }
      setLoading(false);
    };
    load();
  }, [isMockMode, token]);

  // Fetch invitations
  useEffect(() => {
    if (!token || isMockMode) return;
    axios.get(`${API}/contractor/invitations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setInvitations((r.data?.items || []).filter(i => i.status === 'pending')))
      .catch(() => {});
  }, [token, isMockMode]);

  // Fetch usage, weekly stats, profile completion in parallel (real mode only)
  useEffect(() => {
    if (isMockMode || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.allSettled([
      axios.get(`${API}/billing/usage`, { headers }),
      axios.get(`${API}/contractor/weekly-stats`, { headers }),
      axios.get(`${API}/contractor/profile-completion`, { headers }),
      axios.get(`${API}/contractor/activity-streak`, { headers }),
    ]).then(([usageRes, weeklyRes, profileRes, streakRes]) => {
      if (usageRes.status === 'fulfilled') setUsageData(usageRes.value.data);
      if (weeklyRes.status === 'fulfilled') setWeeklyStats(weeklyRes.value.data);
      if (profileRes.status === 'fulfilled') setProfileCompletion(profileRes.value.data);
      if (streakRes.status === 'fulfilled') setActivityStreak(streakRes.value.data);
    });
  }, [isMockMode, token]);

  const handleInvitationResponse = async (inv, action) => {
    setRespondingInv(inv.id);
    try {
      const res = await axios.patch(`${API}/contractor/invitations/${inv.id}`,
        { action }, { headers: { Authorization: `Bearer ${token}` } });
      setInvitations(prev => prev.filter(i => i.id !== inv.id));
      if (action === 'accept' && res.data?.brief_id) {
        navigate(`/app/contractor/brief-board`);
      }
    } catch { /* ignore */ } finally { setRespondingInv(null); }
  };

  // Fetch received direct brief requests separately
  useEffect(() => {
    if (!token) return;
    setReceivedLoading(true);
    axios.get(`${API}/contractor/received-briefs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setReceivedBriefs(res.data?.items || []))
      .catch(() => setReceivedBriefs([]))
      .finally(() => setReceivedLoading(false));
  }, [token]);

  // Load featured status
  useEffect(() => {
    if (!token || isMockMode) return;
    axios.get(`${API}/contractor/featured-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFeaturedStatus(r.data))
      .catch(() => {});
    // Load verified badge status
    axios.get(`${API}/contractor/verified-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setVerifiedStatus(r.data))
      .catch(() => {});
    // Load pending commissions
    axios.get(`${API}/contractor/pending-commissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPendingCommissions(r.data?.pending || []))
      .catch(() => {});
    // Handle return from Stripe
    if (searchParams.get('featured') === 'success') {
      toast.success('Ödeme başarılı! Leaderboard\'da öne çıkıyorsunuz.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (searchParams.get('featured') === 'cancelled') {
      toast('Ödeme iptal edildi.', { icon: '⚠️' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (searchParams.get('verified') === 'success') {
      toast.success('Ödeme başarılı! Doğrulanmış rozet aktifleştirildi.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (searchParams.get('verified') === 'cancelled') {
      toast('Doğrulanmış rozet satın alımı iptal edildi.', { icon: '⚠️' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (searchParams.get('commission') === 'success') {
      toast.success('Komisyon ödendi! Teşekkürler.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (searchParams.get('commission') === 'cancelled') {
      toast('Komisyon ödemesi iptal edildi.', { icon: '⚠️' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [token, isMockMode]);

  const handleGetFeatured = async () => {
    setFeaturedLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/billing/featured-listing`, { origin_url: origin }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ödeme başlatılamadı');
    } finally {
      setFeaturedLoading(false);
    }
  };

  const handleGetVerified = async () => {
    setVerifiedLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/billing/verified-badge`, { origin_url: origin }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ödeme başlatılamadı');
    } finally {
      setVerifiedLoading(false);
    }
  };

  const handlePayCommission = async (proposalId) => {
    setCommissionLoading(p => ({ ...p, [proposalId]: true }));
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/billing/offer-commission`,
        { proposal_id: proposalId, origin_url: origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Komisyon ödemesi başlatılamadı');
    } finally {
      setCommissionLoading(p => ({ ...p, [proposalId]: false }));
    }
  };

  if (loading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_,i) => <div key={i} className="h-28 bg-gray-100 animate-pulse rounded-xl"/>)}
      </div>
    </div>
  );

  const kpis = [
    { label: t('dashboard.activeProjects'),   value: data?.stats.activeProjects,                          icon: FolderKanban, color: 'bg-green-50 text-green-600' },
    { label: t('dashboard.pendingProposals'), value: data?.stats.pendingProposals,                         icon: FileCheck,    color: 'bg-blue-50 text-blue-600' },
    { label: t('dashboard.monthlyRevenue'),   value: `$${((data?.stats.monthlyRevenue||0)/1000).toFixed(0)}K`, icon: DollarSign,   color: 'bg-emerald-50 text-emerald-600' },
    { label: t('dashboard.winRate'),          value: `${data?.stats.winRate}%`,                            icon: TrendingUp,   color: 'bg-purple-50 text-purple-600' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5">
            {isMockMode ? <><span className="w-2 h-2 rounded-full bg-amber-400 inline-block"/>{t('dashboard.mockData')}</> : <><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"/>{t('dashboard.liveData')}</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate('/find-freelancers')} variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 gap-2 shrink-0">
            <Rss className="h-4 w-4 text-red-500"/>Freelancer Bul
          </Button>
          <Button onClick={() => navigate('/app/contractor/brief-board')} className="bg-green-600 hover:bg-green-700 text-white gap-2 shrink-0">
            <Rss className="h-4 w-4"/>{t('dashboard.viewBriefBoard')}
          </Button>
        </div>
      </div>

      {error && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3"><AlertCircle className="h-5 w-5 text-amber-500"/><p className="text-sm text-amber-700">{error}</p></div>}

      {/* Featured Listing Banner */}
      {!isMockMode && featuredStatus && (
        featuredStatus.is_featured ? (
          <div data-testid="featured-active-banner" className="flex items-center gap-4 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 px-5 py-4 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center text-white shrink-0">
              <Star className="h-5 w-5 fill-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-800">Leaderboard'da Öne Çıkıyorsunuz</p>
              <p className="text-xs text-amber-600">
                Bitiş: {featuredStatus.featured_until ? new Date(featuredStatus.featured_until).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
              </p>
            </div>
            <Badge className="bg-amber-500 text-white border-0 shrink-0">Aktif</Badge>
          </div>
        ) : featuredStatus.is_active ? (
          <div data-testid="get-featured-banner" className="flex flex-wrap sm:flex-nowrap items-center gap-4 rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/30 px-5 py-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">Yüklenici Leaderboard'unda Öne Çık</p>
              <p className="text-xs text-gray-500">
                Sergici arayan katılımcılara en üstte görün · {featuredStatus.duration_days} gün boyunca
              </p>
            </div>
            <Button onClick={handleGetFeatured} disabled={featuredLoading} data-testid="get-featured-btn"
              className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white gap-2 font-bold">
              {featuredLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
              Öne Çık — ${featuredStatus.price_usd}
            </Button>
          </div>
        ) : null
      )}

      {/* Verified Badge Banner */}
      {!isMockMode && verifiedStatus && (
        verifiedStatus.is_verified ? (
          <div data-testid="verified-active-banner" className="flex items-center gap-4 rounded-2xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50 px-5 py-4 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white shrink-0">
              <Star className="h-5 w-5 fill-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-emerald-800">Doğrulanmış Firma Rozetiniz Aktif</p>
              <p className="text-xs text-emerald-600">
                Bitiş: {verifiedStatus.verified_until ? new Date(verifiedStatus.verified_until).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
              </p>
            </div>
            <Button
              onClick={() => downloadVerifiedCertificate(
                profileCompletion?.org_name || user?.full_name || 'Firma',
                verifiedStatus.verified_until
              )}
              variant="outline"
              size="sm"
              data-testid="download-certificate-btn"
              className="shrink-0 border-emerald-400 text-emerald-700 hover:bg-emerald-100 gap-1.5 font-semibold"
            >
              <Download className="h-3.5 w-3.5" />
              Sertifika İndir
            </Button>
            <Badge className="bg-emerald-600 text-white border-0 shrink-0">Doğrulandı</Badge>
          </div>
        ) : verifiedStatus.is_active ? (
          <div data-testid="get-verified-banner" className="flex flex-wrap sm:flex-nowrap items-center gap-4 rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50/30 px-5 py-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white shrink-0">
              <Gem className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">Doğrulanmış Firma Rozeti Al</p>
              <p className="text-xs text-gray-500">
                Profilinde güven rozeti · Exhibitor'lara güvenilir firma olarak görün · 1 yıl geçerli
              </p>
            </div>
            <Button onClick={handleGetVerified} disabled={verifiedLoading} data-testid="get-verified-btn"
              className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white gap-2 font-bold">
              {verifiedLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gem className="h-4 w-4" />}
              Rozet Al — ${verifiedStatus.price_usd}/yıl
            </Button>
          </div>
        ) : null
      )}

      {/* Pending Commissions Banner */}
      {!isMockMode && pendingCommissions.length > 0 && (
        <div data-testid="pending-commission-banner" className="rounded-2xl border-2 border-orange-200 bg-orange-50 px-5 py-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center text-white shrink-0">
              <DollarSign className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-bold text-orange-800">Bekleyen Platform Komisyonu</p>
              <p className="text-xs text-orange-600">{pendingCommissions.length} kabul edilmiş teklife komisyon ödenmesi gerekiyor</p>
            </div>
          </div>
          <div className="space-y-2">
            {pendingCommissions.slice(0, 3).map(c => (
              <div key={c.id} className="flex items-center justify-between bg-white rounded-xl px-4 py-2.5 border border-orange-100">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{c.event_name}</p>
                  <p className="text-xs text-gray-400">{c.amount?.toLocaleString()} {c.currency} · Teklif</p>
                </div>
                <Button size="sm" onClick={() => handlePayCommission(c.id)}
                  disabled={!!commissionLoading[c.id]}
                  data-testid={`pay-commission-btn-${c.id}`}
                  className="shrink-0 bg-orange-500 hover:bg-orange-600 text-white gap-1.5 text-xs font-bold ml-3">
                  {commissionLoading[c.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />}
                  ${c.commission_usd} Öde
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Limit Alert Banner */}
      {!limitBannerDismissed && usageData?.usage?.length > 0 && (() => {
        const criticalItem = usageData.usage.find(u => !u.unlimited && u.limit > 0 && (u.current / u.limit) >= 0.8);
        if (!criticalItem) return null;
        const pct = Math.round((criticalItem.current / criticalItem.limit) * 100);
        const isFull = pct >= 100;
        return (
          <div data-testid="limit-alert-banner" className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${isFull ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
            <AlertTriangle className={`h-5 w-5 shrink-0 ${isFull ? 'text-red-500' : 'text-amber-500'}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${isFull ? 'text-red-800' : 'text-amber-800'}`}>
                {isFull ? `Plan limit reached: ${criticalItem.label}` : `Approaching limit: ${criticalItem.label}`}
              </p>
              <p className={`text-xs mt-0.5 ${isFull ? 'text-red-600' : 'text-amber-600'}`}>
                {criticalItem.current}/{criticalItem.limit} {criticalItem.unit} used ({pct}%). Upgrade to submit more proposals.
              </p>
            </div>
            <Button size="sm" onClick={() => navigate('/app/contractor/billing')}
              className={`shrink-0 gap-1.5 text-xs h-8 ${isFull ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'} text-white`}>
              Upgrade <ArrowRight className="h-3 w-3" />
            </Button>
            <button onClick={() => setLimitBannerDismissed(true)} className="shrink-0 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })()}

      {/* Private Invitations Banner */}
      {invitations.length > 0 && (
        <div className="space-y-3" data-testid="invitations-section">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-bold text-gray-800">{t('dashboard.privateInvitations')} ({invitations.length})</h3>
            <span className="flex h-2 w-2 relative"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" /></span>
          </div>
          {invitations.map(inv => (
            <Card key={inv.id} className="border-2 border-blue-200 bg-blue-50/40 shadow-sm" data-testid={`invitation-${inv.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{inv.brief_title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t('dashboard.from')}: {inv.exhibitor_name}</p>
                  {inv.brief_budget?.max > 0 && (
                    <p className="text-xs text-blue-600 font-semibold mt-0.5">
                      {t('dashboard.budget')}: {inv.brief_budget.max?.toLocaleString()} {inv.brief_budget.currency}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" disabled={respondingInv === inv.id}
                    className="h-8 text-xs gap-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleInvitationResponse(inv, 'accept')}>
                    <CheckCircle2 className="w-3 h-3" />{t('dashboard.accept')}
                  </Button>
                  <Button size="sm" variant="outline" disabled={respondingInv === inv.id}
                    className="h-8 text-xs gap-1 border-gray-300 text-gray-600"
                    onClick={() => handleInvitationResponse(inv, 'decline')}>
                    <X className="w-3 h-3" />{t('dashboard.decline')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k,i) => { const Icon = k.icon; return (
          <Card key={i} className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{k.value}</p>
                </div>
                <div className={`p-2.5 rounded-xl ${k.color}`}><Icon className="h-5 w-5"/></div>
              </div>
            </CardContent>
          </Card>
        );})}
      </div>

      {/* Onboarding CTA — show for first-time contractors who skipped or didn't do wizard */}
      {profileCompletion && profileCompletion.score < 30 && !localStorage.getItem('contractor_onboarding_done') && (
        <div className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Wrench className="h-5 w-5" />
            </div>
            <div>
              <p className="font-bold text-sm">{t('dashboard.completeContractorProfile')}</p>
              <p className="text-xs text-green-100 mt-0.5">{t('dashboard.contractorOnboardingDesc')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/app/contractor/onboarding')}
            className="bg-white text-green-700 hover:bg-green-50 font-semibold flex-shrink-0 gap-1.5"
            size="sm"
            data-testid="start-contractor-onboarding-btn"
          >
            {t('dashboard.startSetup')} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Coverage Onboarding Nudge */}
      {!coverageNudgeDismissed && profileCompletion && profileCompletion.missing_fields.includes('Services') && (
        <div data-testid="coverage-nudge-banner" className="flex items-center gap-3 rounded-xl px-4 py-3 border bg-emerald-50 border-emerald-200">
          <Globe className="h-5 w-5 shrink-0 text-emerald-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-800">{t('dashboard.coverageNudge')}</p>
          </div>
          <Button size="sm" onClick={() => navigate('/app/contractor/profile', { state: { tab: 'coverage' } })}
            className="shrink-0 gap-1.5 text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
            {t('dashboard.addCoverage')} <ArrowRight className="h-3 w-3" />
          </Button>
          <button onClick={() => { setCoverageNudgeDismissed(true); localStorage.setItem('coverage_nudge_dismissed', '1'); }}
            className="shrink-0 text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {(weeklyStats || profileCompletion) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Weekly Summary */}
          {weeklyStats && (
            <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50" data-testid="weekly-summary-widget">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2 text-green-800">
                  <Activity className="h-4 w-4" />{t('dashboard.weeklyActivity')}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: t('dashboard.newProposals'), value: weeklyStats.proposals_sent_7d, icon: Send, color: 'text-green-600' },
                    { label: t('dashboard.wonProposals'), value: weeklyStats.wins_7d, icon: Trophy, color: 'text-amber-500' },
                    { label: 'Invites', value: weeklyStats.new_invitations_7d, icon: Mail, color: 'text-blue-600' },
                    { label: t('dashboard.openBriefs'), value: data?.stats?.totalOpenBriefs ?? weeklyStats.open_briefs_count, icon: Rss, color: 'text-purple-600' },
                  ].map(s => (
                    <div key={s.label} className="text-center">
                      <s.icon className={`h-3.5 w-3.5 mx-auto mb-1 ${s.color}`} />
                      <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Profile Completeness */}
          {profileCompletion && (
            <Card className="border-0 shadow-sm" data-testid="profile-completion-widget">
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2 text-gray-800">
                    <Sparkles className="h-4 w-4 text-amber-500" />{t('dashboard.profileCompleteness')}
                  </CardTitle>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${profileCompletion.score >= 80 ? 'bg-emerald-100 text-emerald-700' : profileCompletion.score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                    {profileCompletion.score}%
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                {/* Tier Milestones */}
                <div className="flex items-center justify-between mb-1.5">
                  {[
                    { label: 'Bronze', min: 0,  max: 49,  color: 'text-orange-600', bg: 'bg-orange-100' },
                    { label: 'Silver', min: 50, max: 69,  color: 'text-slate-500',  bg: 'bg-slate-100'  },
                    { label: 'Gold',   min: 70, max: 89,  color: 'text-amber-600',  bg: 'bg-amber-100'  },
                    { label: 'Platin', min: 90, max: 100, color: 'text-blue-600',   bg: 'bg-blue-100'   },
                  ].map(tier => {
                    const active = profileCompletion.score >= tier.min && profileCompletion.score <= tier.max;
                    const passed = profileCompletion.score > tier.max;
                    return (
                      <div key={tier.label} className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-all ${active ? `${tier.bg} ring-1 ring-current ${tier.color}` : passed ? 'opacity-50' : 'opacity-30'}`}>
                        <span className={`text-[10px] font-black ${tier.color}`}>{tier.label}</span>
                        <span className="text-[9px] text-gray-400">{tier.min}%+</span>
                      </div>
                    );
                  })}
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-700 ${profileCompletion.score >= 90 ? 'bg-blue-500' : profileCompletion.score >= 70 ? 'bg-amber-400' : profileCompletion.score >= 50 ? 'bg-slate-400' : 'bg-orange-400'}`}
                    style={{ width: `${profileCompletion.score}%` }}
                  />
                </div>
                {/* Next tier motivation */}
                {profileCompletion.score < 100 && (() => {
                  const nextTarget = profileCompletion.score < 50 ? 50 : profileCompletion.score < 70 ? 70 : profileCompletion.score < 90 ? 90 : 100;
                  const nextLabel  = nextTarget === 50 ? 'Silver' : nextTarget === 70 ? 'Gold' : nextTarget === 90 ? 'Platin' : 'Tam';
                  const needed     = nextTarget - profileCompletion.score;
                  return (
                    <p className="text-[10px] text-gray-500 mb-2">
                      <span className="font-semibold text-amber-600">{nextLabel}</span> seviyesi için <span className="font-semibold">%{needed} daha</span> doldurun!
                    </p>
                  );
                })()}
                {profileCompletion.missing_fields.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 font-medium">{t('dashboard.missing')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profileCompletion.missing_fields.map(f => (
                        <button
                          key={f}
                          onClick={() => {
                            const tabMap = { 'Services': 'services', 'Portfolio projects': 'portfolio', 'Team members': 'team', 'Social links': 'social', 'Company logo': 'overview', 'About description': 'overview' };
                            navigate('/app/contractor/profile', { state: { tab: tabMap[f] || 'overview' } });
                          }}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full hover:bg-amber-100 hover:text-amber-700 transition-colors cursor-pointer"
                        >{f}</button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" />{t('dashboard.profileComplete')}
                  </p>
                )}
                <Button size="sm" variant="outline" onClick={() => {
                    const tabMap = { 'Services': 'services', 'Portfolio projects': 'portfolio', 'Team members': 'team', 'Social links': 'social', 'Company logo': 'overview', 'About description': 'overview' };
                    const firstMissing = profileCompletion?.missing_fields?.[0];
                    const tab = tabMap[firstMissing] || 'overview';
                    navigate('/app/contractor/profile', { state: { tab } });
                  }}
                  className="mt-3 h-7 text-xs gap-1 w-full border-green-200 text-green-700 hover:bg-green-50">
                  <User2 className="h-3 w-3" />{t('dashboard.completeProfile')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Activity Streak Widget */}
      {activityStreak && (
        <Card className="border-0 shadow-sm" data-testid="activity-streak-widget">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flame className={`h-5 w-5 ${activityStreak.current_streak > 0 ? 'text-orange-500' : 'text-gray-300'}`} />
                <span className="font-semibold text-sm text-gray-800">{t('dashboard.activityStreak')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-2xl font-black ${activityStreak.current_streak > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                  {activityStreak.current_streak}
                </span>
                <span className="text-xs text-gray-400 font-medium">{t('dashboard.dayStreak')}</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full ml-1">
                  {activityStreak.active_days_30} {t('dashboard.activeDays')}
                </span>
              </div>
            </div>
            <div className="flex gap-1.5">
              {activityStreak.grid.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className={`w-full h-7 rounded-md transition-colors ${day.active ? 'bg-orange-400' : 'bg-gray-100'}`} />
                  <span className="text-[10px] text-gray-400">{day.label}</span>
                </div>
              ))}
            </div>
            {activityStreak.current_streak === 0 && (
              <p className="text-xs text-gray-400 mt-2 text-center">{t('dashboard.streakPrompt')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* High Match Briefs */}
      {(data?.recentBriefs || []).filter(b => b.match_score != null).length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="high-match-briefs-section">
          <CardHeader className="pb-3 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="h-4 w-4 text-green-500" />
                {t('dashboard.highMatchBriefs')}
                {(data?.recentBriefs || []).filter(b => (b.match_score || 0) >= 80).length > 0 && (
                  <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {(data?.recentBriefs || []).filter(b => (b.match_score || 0) >= 80).length} great match
                  </span>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => navigate('/app/contractor/brief-board')}>
                {t('dashboard.viewAll')} <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-50">
              {(data?.recentBriefs || []).filter(b => b.match_score != null).slice(0, 4).map(b => (
                <div key={b.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                    (b.match_score || 0) >= 80 ? 'bg-emerald-100 text-emerald-700' :
                    (b.match_score || 0) >= 60 ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {b.match_score}%
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{b.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {b.city && <span>{b.city} · </span>}
                      {b.budget_max ? `${b.budget_max.toLocaleString()} ${b.currency}` : 'Budget TBD'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate('/app/contractor/brief-board')}
                    className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white gap-1 shrink-0"
                    data-testid={`quote-brief-${b.id}`}
                  >
                    <Send className="h-3 w-3" />{t('dashboard.quoteNow')}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Brief Recommendations Widget */}
      {!isMockMode && (aiLoading || aiRecs.length > 0) && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-100">
          <CardHeader className="pb-3 border-b border-violet-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-600"/>
                <span className="text-violet-800">AI Brief Önerileri</span>
                {aiLoading && <Loader2 className="h-3 w-3 animate-spin text-violet-400 ml-1"/>}
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-violet-600"
                onClick={() => navigate('/app/contractor/brief-board')}>
                Tümünü Gör <ChevronRight className="h-3 w-3 ml-1"/>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            {aiLoading && aiRecs.length === 0 ? (
              <div className="text-center py-4 text-violet-400">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-1"/>
                <p className="text-xs">Profilinize göre brief'ler analiz ediliyor...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {aiRecs.map(b => (
                  <div key={b.id}
                    className="bg-white rounded-xl p-3 border border-violet-100 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => navigate('/app/contractor/brief-board')}
                    data-testid={`ai-rec-brief-${b.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-black text-violet-700">
                        {'★'.repeat(b.ai_score)}{'☆'.repeat(5 - b.ai_score)}
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        b.ai_score >= 4 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {b.ai_score >= 5 ? 'Mükemmel' : b.ai_score >= 4 ? 'Yüksek' : 'Orta'} Uyum
                      </span>
                    </div>
                    <p className="text-xs font-bold text-gray-900 truncate">{b.title}</p>
                    {(b.city || b.country) && (
                      <p className="text-[10px] text-gray-400 mt-0.5">{[b.city, b.country].filter(Boolean).join(', ')}</p>
                    )}
                    {b.budget_max && (
                      <p className="text-[10px] font-semibold text-gray-600 mt-0.5">
                        {b.budget_max.toLocaleString()} {b.currency}
                      </p>
                    )}
                    {b.ai_reason && (
                      <p className="text-[10px] text-violet-600 mt-1.5 line-clamp-2">{b.ai_reason}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-green-500"/>Monthly Revenue Trend</CardTitle>
          </CardHeader>          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data?.revenueChart||[]} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="month" tick={{fontSize:11,fill:'#9ca3af'}} axisLine={false}/>
                <YAxis tick={{fontSize:11,fill:'#9ca3af'}} axisLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}K`}/>
                <Tooltip formatter={v=>[`$${v.toLocaleString()}`,'Revenue']}/>
                <Bar dataKey="revenue" radius={[6,6,0,0]}>
                  {(data?.revenueChart||[]).map((_,i,arr)=><Cell key={i} fill={i===arr.length-1?'#16a34a':'#bbf7d0'}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><FolderKanban className="h-4 w-4 text-green-500"/>{t('dashboard.activeProjectsList')}</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={()=>navigate('/app/contractor/projects')}>{t('dashboard.viewAll')} <ChevronRight className="h-3 w-3 ml-1"/></Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-50">
              {(data?.activeProjects||[]).map(p => {
                const sc = STATUS_CONFIG[p.status]||STATUS_CONFIG.in_progress;
                return (
                  <div key={p.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{p.brief}</p>
                        <p className="text-xs text-gray-400">{p.client}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5"><div className="bg-green-500 h-1.5 rounded-full" style={{width:`${p.progress}%`}}/></div>
                      <span className="text-xs text-gray-500 shrink-0">{p.progress}%</span>
                      <span className="text-xs font-mono text-gray-700">{p.value?.toLocaleString()} {p.currency}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3 border-b border-gray-50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Inbox className="h-4 w-4 text-green-500"/>
              {t('dashboard.directQuoteRequests')}
              {receivedBriefs.length > 0 && (
                <span className="ml-1 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">{receivedBriefs.length}</span>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {receivedLoading ? (
            <div className="p-6 text-center text-sm text-gray-400">Loading...</div>
          ) : receivedBriefs.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Inbox className="h-6 w-6 text-gray-400"/>
              </div>
              <p className="text-sm font-semibold text-gray-700">No direct quote requests yet</p>
              <p className="text-xs text-gray-400 mt-1">Exhibitors can send you direct briefs from your public profile page.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {receivedBriefs.map(b => (
                <div key={b.id} data-testid={`received-brief-${b.id}`} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="h-3.5 w-3.5 text-gray-400 flex-shrink-0"/>
                        <p className="text-sm font-semibold text-gray-900 truncate">{b.exhibitor_name}</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${b.status === 'OPEN' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {b.status}
                        </span>
                      </div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">{b.event?.show || '—'}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3"/>{b.event?.city}{b.event?.country ? `, ${b.event.country}` : ''}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3"/>{b.event?.month || '—'}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-gray-900">
                        {b.budget?.max ? `${b.budget.max.toLocaleString()} ${b.budget.currency || ''}` : '—'}
                      </p>
                      <p className="text-xs text-gray-400">{b.created_at || '—'}</p>
                    </div>
                  </div>
                  {b.notes && (
                    <p className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 italic line-clamp-2">"{b.notes}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deneyimini Paylaş Kartı */}
      {!localStorage.getItem('contractor_testimonial_submitted') && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-100">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
              <MessageSquare className="h-5 w-5 text-teal-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">ExpoStand deneyiminizi paylaşın</p>
              <p className="text-xs text-gray-500 mt-0.5">Yorum yazmak projelerinizin güvenilirliğini artırır.</p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowTestimonialForm(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white shrink-0 gap-1.5"
              data-testid="contractor-write-review-btn"
            >
              <Star className="h-3.5 w-3.5" />
              Yorum Yaz
            </Button>
          </CardContent>
        </Card>
      )}

      {showTestimonialForm && (
        <TestimonialForm
          user={user}
          onClose={() => {
            setShowTestimonialForm(false);
            localStorage.setItem('contractor_testimonial_submitted', '1');
          }}
        />
      )}
    </div>
  );
};

export default ContractorDashboard;
